import API_BASE from '../config';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Zap, Users, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { apiService } from '../services/apiService';
import LinkedInImport from '../components/LinkedInImport';
import WhatsAppVerifier from '../components/WhatsAppVerifier';
import PhoneCountryPicker from '../components/PhoneCountryPicker';
import { detectCountryFromInput } from '../utils/phoneCountries';

const API_URL = API_BASE;

// Design tokens — matches the landing page / About page palette
// (Poppins + purple, extracted from Claude Design artifact 33bd7d1f) so the
// analyze screen reads as the same product as the page the user just left.
const colors = {
  primary: '#7c3aed',
  primaryHover: '#6d28d9',
  primaryLight: '#f6f4fd',
  navy: '#1a0c38',
  dark: '#1a1130',
  body: '#6b6580',
  background: '#f8f7fc',
  surface: '#FFFFFF',
  success: '#16a34a',
  danger: '#DC2626',
  border: '#efedf6'
};

// Official brand logo asset (frontend/public/images/brand/) — same file
// used verbatim on every screen. Only `height` is set; width stays `auto`
// so the image's own aspect ratio is preserved and never distorted.
const Logo = ({ height = 24 }) => (
  <img
    src="/images/brand/successlane-logo-mark.png"
    alt="SuccessLane"
    className="sl-logo"
    style={{ height, width: 'auto', display: 'block' }}
  />
);

// LinkedIn glyph. fill="currentColor" so it takes the colour of whatever
// it sits inside — white on the active tab, muted on the inactive one.
const LinkedInIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
  </svg>
);

// CTA Button - Successlane purple brand gradient
const CTAButton = ({ children, onClick, disabled, className = '', type = 'button', ...props }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`
      text-white font-bold text-lg
      px-10 py-[18px] rounded-lg border-none cursor-pointer
      shadow-[0_4px_20px_rgba(124,58,237,.35)]
      transition-all duration-200 ease-in-out
      hover:-translate-y-[1px] hover:shadow-[0_6px_24px_rgba(124,58,237,.45)]
      disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0
      w-full
      ${className}
    `}
    style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontFamily: "'Poppins', sans-serif" }}
    {...props}
  >
    {children}
  </button>
);

// Rotating non-tech examples — placeholder disappears when the user types.
const ROLE_EXAMPLES = [
  'e.g. Senior Finance Manager',
  'e.g. Regional Sales Head',
  'e.g. Supply Chain Lead',
  'e.g. Brand Marketing Manager',
  'e.g. HR Business Partner',
];

const COMPANY_EXAMPLES = [
  'e.g. HUL, ITC, Nestle',
  'e.g. FMCG, Pharma, Retail',
  'e.g. Reliance, Asian Paints, Godrej',
  'e.g. Banking, Insurance, Consulting',
  'e.g. Titan, Britannia, Dabur',
];

const useRotatingPlaceholder = (examples, intervalMs = 2800) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % examples.length), intervalMs);
    return () => clearInterval(t);
  }, [examples, intervalMs]);
  return examples[idx];
};

const InputForm = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = 'Get your free LinkedIn evaluation — Successlane'; }, []);
  // Same live-pricing fetch as the landing page, so the quota-exhausted
  // upgrade wall shows the current price instead of a stale hardcoded one.
  const [displayPrice, setDisplayPrice] = useState(1499);
  useEffect(() => {
    fetch(`${API_URL}/api/pricing/current`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const p = data?.offer_price || data?.price || data?.standard_price;
        if (p && Number.isFinite(Number(p))) setDisplayPrice(Number(p));
      })
      .catch(() => {});
  }, []);
  const [inputMethod, setInputMethod] = useState('url'); // 'url' or 'pdf'
  const [formData, setFormData] = useState({
    file: null,
    targetRole: '',
    targetCompany: ''
  });
  const [importedProfile, setImportedProfile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');   // full E.164 like "+919003454300"
  const [phoneCountryDial, setPhoneCountryDial] = useState('+91');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [whatsappVerified, setWhatsappVerified] = useState(false);

  // Keep whatsappNumber in sync as either sub-field changes so downstream
  // navigation state + sessionStorage always carry the full E.164 value.
  useEffect(() => {
    const full = phoneLocal ? `${phoneCountryDial}${phoneLocal}` : '';
    setWhatsappNumber(full);
  }, [phoneCountryDial, phoneLocal]);

  // On mount, restore the quota-exhausted popup if the user was just bounced
  // back here from /loading with a 402 response. AnalysisLoading writes the
  // detail payload into sessionStorage; we read + clear it here so the popup
  // fires once and doesn't re-appear on subsequent visits.
  useEffect(() => {
    const raw = sessionStorage.getItem('quotaExhausted');
    if (!raw) return;
    try {
      const detail = JSON.parse(raw);
      setQuotaExhausted(true);
      if (detail?.route === 'url' || detail?.route === 'pdf') {
        setBlockedRoute(detail.route);
      }
    } catch (_) {
      // Malformed — just show the generic popup without route-specific copy
      setQuotaExhausted(true);
    } finally {
      sessionStorage.removeItem('quotaExhausted');
    }
  }, []);

  // REMOVED: a live, as-you-type quota check used to live here, firing the
  // quota-exhausted popup the moment someone finished typing an already-used
  // WhatsApp number — before OTP, before any verification. Per spec, a
  // number that already used its free evaluation should still go through
  // OTP first; verifying who they are is what lets the system recognize
  // them and show their *existing report* with an upgrade prompt, not just
  // a bare paywall popup. This check firing on every keystroke short-
  // circuited that every time, so nobody could ever reach the OTP screen
  // with an already-used number to begin with. The route-specific
  // server-side gate at analyze-profile / evaluate remains the real
  // backstop with this removed.

  const handlePhoneChange = (rawValue) => {
    // Auto-split country + local when user types/pastes "+<cc>...".
    const parsed = detectCountryFromInput(rawValue, phoneCountryDial);
    if (parsed.countryDial !== phoneCountryDial) {
      setPhoneCountryDial(parsed.countryDial);
    }
    setPhoneLocal(parsed.localNumber);
  };
  const [quotaRemaining, setQuotaRemaining] = useState(null);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [blockedRoute, setBlockedRoute] = useState(null); // 'url' | 'pdf' | null
  const pendingSubmitRef = useRef(null);
  const [returningStatus, setReturningStatus] = useState(null); // null | 'subscribed_delivered' | 'subscribed_pending'
  const [returningOrderId, setReturningOrderId] = useState(null);
  const [userMessage, setUserMessage] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  const rolePlaceholder = useRotatingPlaceholder(ROLE_EXAMPLES);
  const companyPlaceholder = useRotatingPlaceholder(COMPANY_EXAMPLES);

  // Used by the loading-screen OTP flow in the next step; no longer called from the form.
  const handleWhatsAppVerified = (phone, quota) => {
    setWhatsappNumber(phone);
    setWhatsappVerified(true);
    if (quota) {
      setQuotaRemaining(quota.remaining);
      if (quota.remaining <= 0) setQuotaExhausted(true);
    }
  };

  // NOTE: returning-user check moved to the loading screen (fires after OTP verify).
  // Disabled here because verification no longer happens on the form.
  // useEffect(() => {
  //   if (!whatsappVerified || !whatsappNumber) return;
  //   const linkedinUrl = importedProfile?.linkedin_url || null;
  //   fetch(`${API_URL}/api/user/returning-check?whatsapp=${encodeURIComponent(whatsappNumber)}${linkedinUrl ? `&linkedin_url=${encodeURIComponent(linkedinUrl)}` : ''}`)
  //     .then(r => r.json())
  //     .then(data => {
  //       if (data.status === 'subscribed_delivered') {
  //         setReturningStatus('subscribed_delivered');
  //         setReturningOrderId(data.order_id || null);
  //       } else if (data.status === 'subscribed_pending') {
  //         setReturningStatus('subscribed_pending');
  //         setReturningOrderId(data.order_id || null);
  //       }
  //       // quota_exhausted is already handled by the WhatsApp verifier quota response
  //     })
  //     .catch(() => {}); // non-fatal — don't block the form
  // }, [whatsappVerified, whatsappNumber, importedProfile]);

  const handleSendMessage = async () => {
    if (!userMessage.trim()) return;
    setMessageSending(true);
    try {
      await fetch(`${API_URL}/api/user/send-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: importedProfile?.full_name || null,
          whatsapp_number: whatsappNumber,
          linkedin_url: importedProfile?.linkedin_url || null,
          order_id: returningOrderId,
          message: userMessage.trim()
        })
      });
      setMessageSent(true);
    } catch {
      // best-effort, show sent anyway
      setMessageSent(true);
    } finally {
      setMessageSending(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }
    setFormData(prev => ({ ...prev, file }));
    setFileName(file.name);
    setError('');
  };

  const handleProfileImported = (profileData, profileId) => {
    setImportedProfile({ ...profileData, profileId });
    setError('');
  };

  // Run evaluation on imported profile (URL path).
  // Stores params for the loading screen and navigates — eval fires there after OTP.
  const runEvaluation = () => {
    if (!importedProfile) { setError('Please import your LinkedIn profile first'); return; }
    if (!formData.targetRole) { setError('Please enter your target role to run evaluation'); return; }

    sessionStorage.setItem('evaluationParams', JSON.stringify({
      profile_data: importedProfile,
      target_role: formData.targetRole,
      target_company: formData.targetCompany || null,
      linkedin_url: importedProfile.linkedin_url || null,
      whatsapp_number: whatsappNumber,
    }));
    sessionStorage.setItem('userInput', JSON.stringify({
      targetRole: formData.targetRole,
      targetCompany: formData.targetCompany,
      whatsappNumber,
    }));
    // replace: true so /loading doesn't stack a new history entry on top of
    // /analyze — pressing back from anywhere further into the funnel should
    // collapse straight back to the landing page, not thread back through
    // every intermediate step (some of which, like this OTP screen, aren't
    // meaningful to revisit anyway).
    navigate('/loading', { replace: true });
  };

  const handleGetRewrite = () => {
    navigate('/checkout', { replace: true });
  };

  // Called when the user clicks "Get My Free Evaluation".
  // Validates inputs, then navigates directly to the loading screen.
  // OTP verification happens on the loading screen; the server-side quota
  // gate at analyze-profile / evaluate is still the final backstop.
  const handleCtaClick = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (inputMethod === 'pdf' && !formData.file) { setError('Please upload your LinkedIn profile PDF'); return; }
    if (inputMethod === 'url' && !importedProfile) { setError('Please import your LinkedIn profile first'); return; }
    if (!formData.targetRole.trim()) { setError('Please enter your target role'); return; }
    if (!whatsappValid) { setError('Please enter your WhatsApp number'); return; }

    setError('');

    // No pre-submit quota block here on purpose. The intended flow (per
    // spec): a number that already used its free evaluation still goes
    // through OTP on the next screen — verifying who they are — and *then*
    // gets recognized and shown their existing report with an upgrade
    // prompt (AnalysisLoading.jsx's verifyOtp(), returning-check's
    // quota_exhausted branch). Blocking here, before OTP, short-circuited
    // that and only ever showed a bare "quota used" paywall popup with no
    // report attached — even for a number that has one. The route-specific
    // server-side gate at analyze-profile / evaluate is still the final
    // backstop either way, so nothing is actually left unprotected by
    // removing this early check.
    if (inputMethod === 'url') {
      runEvaluation();
    } else {
      submitPdfFlow();
    }
  };

  // PDF flow: store userInput, then navigate to /loading with the file in Router state.
  // Upload happens on the loading screen after OTP verification.
  const submitPdfFlow = async () => {
    try {
      setLoading(true);
      sessionStorage.setItem('userInput', JSON.stringify({
        targetRole: formData.targetRole,
        targetCompany: formData.targetCompany,
        whatsappNumber,
      }));
      // replace: true — same reasoning as the URL-flow navigate() above. Also
      // closes a separate bug: location.state (carrying the File object) is
      // NOT reliably restored by the browser if this entry is ever returned
      // to via back navigation after a further push — confirmed directly:
      // history.state comes back completely null in that case, not just
      // missing the file. A replace here means there's no later entry that
      // could try to "return" to this one in the first place.
      navigate('/loading', {
        replace: true,
        state: {
          pdfFile: formData.file,
          targetRole: formData.targetRole,
          targetCompany: formData.targetCompany,
          whatsappNumber,
        },
      });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => handleCtaClick(e);

  // Validation runs against the local-number field directly (not the combined
  // string, which is empty briefly between renders). Require at least 6 digits.
  const whatsappValid = (phoneLocal || '').replace(/\D/g, '').length >= 6;

  return (
    <div className="sl-analyze-page min-h-screen pt-6 sm:pt-12 pb-12 px-4" style={{ backgroundColor: colors.background, fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Google Fonts — Poppins matches the landing / About page brand */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        /* App.css sets font-family via a bare "*" selector, which directly
           matches every element (including this page's own text) and wins
           over an inherited font-family from this root div regardless of
           the ancestor's specificity — a direct match always beats
           inheritance. Without this, every element here silently renders
           in Inter instead of Poppins. Same fix as CheckoutPage.jsx /
           IntakeForm2.jsx. */
        .sl-analyze-page, .sl-analyze-page * { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; }
        .sl-cta-hover:hover { transform: translateY(-1px); box-shadow: 0 18px 40px rgba(124,58,237,.4); }
        @media (max-width: 480px) {
          .sl-analyze-header h1 { font-size: 18px !important; }
        }
      `}</style>

      <div className="max-w-[520px] mx-auto">
        {/* Back with Logo */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm hover:opacity-70 transition-opacity"
            style={{ color: colors.body }}
          >
            ← Back
          </button>
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <Logo />
          </div>
        </div>

        {/* Header - Successlane brand style */}
        <div className="text-center mb-4 sl-analyze-header">
          <h1
            className="text-xl sm:text-2xl md:text-3xl font-bold mb-3"
            style={{ color: colors.navy, letterSpacing: '-.5px' }}
          >
            Let's see what recruiters see when they find your profile.
          </h1>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-4 sm:p-8 border" style={{ borderColor: colors.border }}>
          <form onSubmit={handleSubmit} className="space-y-3">
            
            {error && (
              <div 
                className="flex items-start gap-3 p-4 rounded-lg border"
                style={{ backgroundColor: `${colors.danger}10`, borderColor: `${colors.danger}30` }}
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.danger }} />
                <span className="text-sm" style={{ color: colors.danger }}>{error}</span>
              </div>
            )}

            {/* Input Method Toggle */}
            <div>
              <div
                className="flex w-full p-1 rounded-full border"
                style={{ borderColor: colors.border, backgroundColor: colors.surface }}
              >
                <button
                  type="button"
                  onClick={() => { setInputMethod('url'); }}
                  className="flex-1 flex items-center justify-center gap-1.5 min-h-[48px] px-2 rounded-full font-medium text-sm transition-all"
                  style={{
                    backgroundColor: inputMethod === 'url' ? colors.primary : 'transparent',
                    color: inputMethod === 'url' ? '#fff' : colors.body,
                  }}
                  data-testid="method-url-btn"
                >
                  <LinkedInIcon className="w-4 h-4 shrink-0" />
                  LinkedIn URL
                </button>
                <button
                  type="button"
                  onClick={() => setInputMethod('pdf')}
                  className="flex-1 flex items-center justify-center gap-1.5 min-h-[48px] px-2 rounded-full font-medium text-sm transition-all"
                  style={{
                    backgroundColor: inputMethod === 'pdf' ? colors.primary : 'transparent',
                    color: inputMethod === 'pdf' ? '#fff' : colors.body,
                  }}
                  data-testid="method-pdf-btn"
                >
                  <LinkedInIcon className="w-4 h-4 shrink-0" />
                  LinkedIn PDF
                </button>
              </div>
            </div>

            {/* LinkedIn URL Import */}
            {inputMethod === 'url' && (
              <div className="pt-2">
                <LinkedInImport
                  onProfileImported={handleProfileImported}
                  onProfileDataChange={(data) => setImportedProfile(data)}
                  onFallbackToPdf={() => setInputMethod('pdf')}
                  whatsappNumber={whatsappNumber}
                />
              </div>
            )}

            {/* PDF Upload - Only show if PDF method selected */}
            {inputMethod === 'pdf' && (
              <div className="space-y-4">
                <div
                  className="border-2 border-dashed rounded-xl py-6 px-4 text-center cursor-pointer transition-colors hover:border-[#7c3aed]"
                  style={{ borderColor: fileName ? colors.primary : colors.border }}
                >
                  <input
                    type="file"
                    id="profile-pdf"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <label htmlFor="profile-pdf" className="cursor-pointer">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                      style={{ backgroundColor: colors.primaryLight, color: colors.primary }}
                    >
                      <LinkedInIcon className="w-6 h-6" />
                    </div>
                    {fileName ? (
                      <p
                        className="text-sm font-medium px-2"
                        style={{ color: colors.dark, wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                      >
                        {fileName}
                      </p>
                    ) : (
                      <>
                        <p className="font-medium mb-1" style={{ color: colors.dark }}>Upload your LinkedIn PDF</p>
                        <p className="text-sm" style={{ color: colors.body }}>
                          LinkedIn → Profile → More → Save to PDF
                        </p>
                        <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-amber-600">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                          Resumes won't work here. This must be your LinkedIn PDF
                        </p>
                      </>
                    )}
                  </label>
                </div>

                {formData.file && (
                  <div className="text-sm text-slate-600 mt-2">
                    ✓ Uploaded — {formData.file.name}
                  </div>
                )}

              </div>
            )}

            {/* Target Role — rotating non-tech placeholder, role only (no company) */}
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: colors.dark }}>
                What role are you targeting?
              </label>
              <input
                type="text"
                placeholder={rolePlaceholder}
                value={formData.targetRole}
                onChange={(e) => setFormData({ ...formData, targetRole: e.target.value })}
                className="w-full min-h-[48px] px-4 py-3 rounded-lg border text-base transition-colors focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                style={{ borderColor: colors.border, color: colors.dark }}
                data-testid="target-role-input"
              />
              <p className="text-xs mt-2" style={{ color: colors.body }}>
                Role only, no company or location
              </p>
            </div>

            {/* Target Industry or Target Company — rotating non-tech placeholder */}
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: colors.dark }}>
                Target Industry or Target Company <span style={{ color: colors.body }}>(optional)</span>
              </label>
              <input
                type="text"
                placeholder={companyPlaceholder}
                value={formData.targetCompany}
                onChange={(e) => setFormData({ ...formData, targetCompany: e.target.value })}
                className="w-full min-h-[48px] px-4 py-3 rounded-lg border text-base transition-colors focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                style={{ borderColor: colors.border, color: colors.dark }}
                data-testid="target-company-input"
              />
              <p className="text-xs mt-2" style={{ color: colors.body }}>
                Up to 3, comma-separated
              </p>
            </div>

            {/* WhatsApp number — OTP verification happens on the loading screen */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                WhatsApp Number
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <PhoneCountryPicker
                  value={phoneCountryDial}
                  onChange={setPhoneCountryDial}
                  testId="analyze-phone-country"
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phoneLocal}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onPaste={(e) => {
                    const paste = (e.clipboardData || window.clipboardData).getData('text');
                    if (paste && paste.trim().startsWith('+')) {
                      e.preventDefault();
                      handlePhoneChange(paste);
                    }
                  }}
                  placeholder="9876543210"
                  className="flex-1 min-w-0 min-h-[48px] px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none"
                  data-testid="analyze-phone"
                />
              </div>
              <p className="mt-1 text-xs" style={{ color: colors.body }}>
                We'll send a verification code here
              </p>
            </div>

            {/* Submit — single CTA, gated on all validations + WhatsApp verify */}
            <div className="space-y-3">
              {/* Trust + consent copy */}
              <div className="text-center">
                <p className="text-[11px] text-slate-400">
                  🛡️ By clicking Evaluate, you agree to our <a href="/terms" className="underline hover:text-slate-600">Terms &amp; Conditions</a> and to receive service updates on this WhatsApp number.
                </p>
              </div>
              <CTAButton
                type="button"
                onClick={handleCtaClick}
                disabled={
                  loading ||
                  (inputMethod === 'url' && !importedProfile) ||
                  (inputMethod === 'pdf' && !formData.file) ||
                  !whatsappValid ||
                  !formData.targetRole.trim()
                }
                data-testid="submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting Analysis...
                  </span>
                ) : (
                  (inputMethod === 'url' && !importedProfile) ? 'Import Profile First' :
                  (inputMethod === 'pdf' && !formData.file) ? 'Upload PDF First' :
                  !formData.targetRole.trim() ? 'Enter Target Role' :
                  !whatsappValid ? 'Enter your WhatsApp number' :
                  'Get My Free Evaluation →'
                )}
              </CTAButton>
              {quotaRemaining !== null && !quotaExhausted && (
                <p className="text-xs text-center" style={{ color: colors.body }} data-testid="quota-remaining-note">
                  {quotaRemaining} free evaluation{quotaRemaining === 1 ? '' : 's'} remaining for this number
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Trust Signals - Blue accent */}
        <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2">
          <div className="flex items-center gap-1.5" style={{ color: colors.body }}>
            <Lock className="w-4 h-4 shrink-0" style={{ color: colors.primary }} />
            <span className="text-xs">Processed securely, never shared</span>
          </div>
          <div className="flex items-center gap-1.5" style={{ color: colors.body }}>
            <Zap className="w-4 h-4 shrink-0" style={{ color: colors.primary }} />
            <span className="text-xs">Results in 60 seconds</span>
          </div>
          <div className="flex items-center gap-1.5" style={{ color: colors.body }}>
            <Users className="w-4 h-4 shrink-0" style={{ color: colors.primary }} />
            <span className="text-xs">500+ professionals evaluated this month</span>
          </div>
        </div>
        {/* Quota-exhausted upgrade wall */}
        {quotaExhausted && (() => {
          const isUrl = blockedRoute === 'url';
          const headline = isUrl
            ? "You've seen the score. Now it's time to change it."
            : "One honest look was enough. Now let's fix it.";
          const body = isUrl
            ? "Your free evaluation just showed you exactly what recruiters see — and what makes them move on. Every gap it found has a fix. Our recruiter-certified rewrite rewrites every section, top to bottom, and has it ready in 48 hours. Written by the people who actually do the hiring."
            : "You've had your free evaluation. You know the score, the gaps, the missed opportunities. The question now isn't whether your profile needs work — it's who's going to do it. Our recruiters will. In 48 hours.";
          const cta = isUrl ? "Get My Profile Rewritten →" : "Let Recruiters Rewrite Mine →";
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(13,31,60,0.65)' }}
              data-testid="quota-exhausted-overlay"
            >
              <div className="max-w-md w-full rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: colors.surface }}>
                {/* Dark header */}
                <div className="p-6 pb-5" style={{ backgroundColor: colors.navy, color: '#fff' }}>
                  <p className="text-[10px] font-semibold tracking-widest uppercase mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {isUrl ? 'LinkedIn URL evaluation used' : 'PDF evaluation used'}
                  </p>
                  <h3 className="text-xl font-bold leading-snug" data-testid="quota-exhausted-title">
                    {headline}
                  </h3>
                  <p className="text-sm mt-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    {body}
                  </p>
                </div>

                {/* Price badge */}
                <div className="px-6 pt-5">
                  <div
                    className="flex items-center justify-between px-4 py-3 rounded-lg mb-4"
                    style={{ backgroundColor: colors.primaryLight }}
                  >
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.primary }}>Recruiter-Certified Rewrite</p>
                      <p className="text-xs mt-0.5" style={{ color: colors.body }}>Every section · Delivered in 48 hours</p>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: colors.navy }}>₹{displayPrice.toLocaleString('en-IN')}</p>
                  </div>

                  <button
                    type="button"
                    onClick={handleGetRewrite}
                    className="w-full py-3.5 rounded-lg font-bold text-sm text-white transition-all"
                    style={{ backgroundColor: colors.primary }}
                    data-testid="quota-exhausted-upgrade-btn"
                  >
                    {cta}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="w-full py-2.5 mt-2 rounded-lg font-medium text-sm"
                    style={{ color: colors.body }}
                    data-testid="quota-exhausted-home-btn"
                  >
                    Not now, back to home
                  </button>
                </div>

                {/* Trust footnote */}
                <p className="text-center text-xs pb-5 mt-1" style={{ color: colors.body }}>
                  Written by working recruiters · One-time payment · No subscription

                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Returning user: order delivered — existing customer message box */}
      {returningStatus === 'subscribed_delivered' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(13,31,60,0.65)' }}>
          <div className="max-w-md w-full rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: colors.surface }}>
            <div className="p-6 pb-5" style={{ backgroundColor: colors.navy, color: '#fff' }}>
              <p className="text-[10px] font-semibold tracking-widest uppercase mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Existing Customer</p>
              <h3 className="text-xl font-bold leading-snug">Welcome back! Your profile rewrite was delivered.</h3>
              <p className="text-sm mt-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                It looks like we've already delivered your recruiter-certified rewrite. If you have questions or need a revision, send us a message below — our team will get back to you on WhatsApp.
              </p>
            </div>
            <div className="p-6">
              {messageSent ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#D1FAE5' }}>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24"><path stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <p className="font-semibold" style={{ color: colors.navy }}>Message sent!</p>
                  <p className="text-sm mt-1" style={{ color: colors.body }}>We'll reply on your WhatsApp shortly.</p>
                  <button onClick={() => setReturningStatus(null)} className="mt-4 text-sm underline" style={{ color: colors.body }}>
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <textarea
                    rows={4}
                    placeholder="What do you need help with? E.g. 'Can I get a revision on my headline?'"
                    value={userMessage}
                    onChange={e => setUserMessage(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
                    style={{ borderColor: colors.border, color: colors.dark }}
                  />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setReturningStatus(null)} className="flex-1 py-3 rounded-lg text-sm font-medium" style={{ border: `1px solid ${colors.border}`, color: colors.body }}>
                      Cancel
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={messageSending || !userMessage.trim()}
                      className="flex-1 py-3 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {messageSending ? 'Sending…' : 'Send Message'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Returning user: order in progress — service pending */}
      {returningStatus === 'subscribed_pending' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(13,31,60,0.65)' }}>
          <div className="max-w-md w-full rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: colors.surface }}>
            <div className="p-6 pb-5" style={{ backgroundColor: colors.navy, color: '#fff' }}>
              <p className="text-[10px] font-semibold tracking-widest uppercase mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Order In Progress</p>
              <h3 className="text-xl font-bold leading-snug">Your rewrite is on its way!</h3>
              <p className="text-sm mt-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                We've already received your order and our recruiter is working on your profile. You'll receive the completed rewrite on your WhatsApp within 48 hours of placing the order.
              </p>
            </div>
            <div className="p-6">
              <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: colors.primaryLight }}>
                <p className="text-sm font-medium" style={{ color: colors.primary }}>Have a question about your order?</p>
                <p className="text-xs mt-1" style={{ color: colors.body }}>Send us a message and we'll reply on WhatsApp.</p>
              </div>
              {messageSent ? (
                <div className="text-center py-4">
                  <p className="font-semibold" style={{ color: colors.navy }}>Message sent!</p>
                  <p className="text-sm mt-1" style={{ color: colors.body }}>We'll reply on your WhatsApp shortly.</p>
                </div>
              ) : (
                <>
                  <textarea
                    rows={3}
                    placeholder="E.g. 'When will my rewrite be ready?' or 'I'd like to update my target role.'"
                    value={userMessage}
                    onChange={e => setUserMessage(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
                    style={{ borderColor: colors.border, color: colors.dark }}
                  />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setReturningStatus(null)} className="flex-1 py-3 rounded-lg text-sm font-medium" style={{ border: `1px solid ${colors.border}`, color: colors.body }}>
                      Close
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={messageSending || !userMessage.trim()}
                      className="flex-1 py-3 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {messageSending ? 'Sending…' : 'Send Message'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputForm;
